const WORD_CHAR_PATTERN = /[A-Za-z0-9]/;

export const MIN_HIGHLIGHT_LENGTH = 3;

const collapseWhitespace = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const escapeForRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isWordChar = (value = '') => WORD_CHAR_PATTERN.test(value);

const hasWordBoundaries = (text, start, end) => {
    const matchedText = text.slice(start, end);
    const firstChar = matchedText[0] || '';
    const lastChar = matchedText[matchedText.length - 1] || '';
    const beforeChar = start > 0 ? text[start - 1] : '';
    const afterChar = end < text.length ? text[end] : '';

    if (isWordChar(firstChar) && isWordChar(beforeChar)) return false;
    if (isWordChar(lastChar) && isWordChar(afterChar)) return false;
    return true;
};

const createSearchPattern = (value) =>
    escapeForRegExp(collapseWhitespace(value)).replace(/\s+/g, '\\s+');

export const normalizeHighlightValue = (value = '') => collapseWhitespace(value);

export const normalizeHighlightTokens = (
    tokens = [],
    {minLength = MIN_HIGHLIGHT_LENGTH} = {},
) => {
    const seen = new Set();

    return tokens
        .map((token) => ({
            ...token,
            resolved: normalizeHighlightValue(token?.resolved || ''),
            original: normalizeHighlightValue(token?.original || token?.resolved || ''),
        }))
        .filter((token) => token.resolved.length >= minLength)
        .filter((token) => {
            const key = token.resolved.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

export const splitHighlightPhrases = (value = '') =>
    String(value)
        .split(/[,;\n]+/)
        .map((part) => collapseWhitespace(part))
        .filter(Boolean);

export const findHighlightRanges = (
    text = '',
    highlightTokens = [],
    {minLength = MIN_HIGHLIGHT_LENGTH} = {},
) => {
    if (!text) return [];

    const normalizedTokens = normalizeHighlightTokens(highlightTokens, {minLength});
    if (!normalizedTokens.length) return [];

    const candidates = [];

    normalizedTokens.forEach((token) => {
        const pattern = createSearchPattern(token.resolved);
        if (!pattern) return;

        const regex = new RegExp(pattern, 'gi');
        let match = regex.exec(text);

        while (match) {
            const start = match.index;
            const matchedText = match[0];
            const end = start + matchedText.length;

            if (hasWordBoundaries(text, start, end)) {
                candidates.push({
                    start,
                    end,
                    token,
                });
            }

            if (regex.lastIndex === match.index) {
                regex.lastIndex += 1;
            }
            match = regex.exec(text);
        }
    });

    candidates.sort((left, right) => {
        if (left.start !== right.start) return left.start - right.start;
        const leftLength = left.end - left.start;
        const rightLength = right.end - right.start;
        return rightLength - leftLength;
    });

    const accepted = [];

    candidates.forEach((candidate) => {
        const overlaps = accepted.some(
            (range) =>
                candidate.start < range.end &&
                candidate.end > range.start,
        );

        if (!overlaps) accepted.push(candidate);
    });

    return accepted.sort((left, right) => left.start - right.start);
};

export const restoreFormulaTextFromHighlights = (
    text = '',
    highlightTokens = [],
    {minLength = 1} = {},
) => {
    if (!text) return text;

    const formulaTokens = normalizeHighlightTokens(highlightTokens, {minLength}).filter(
        (token) =>
            token.original &&
            token.original.includes('{{') &&
            token.original !== token.resolved,
    );
    if (!formulaTokens.length) return text;

    const ranges = findHighlightRanges(text, formulaTokens, {minLength});
    if (!ranges.length) return text;

    let restored = text;

    [...ranges].reverse().forEach((range) => {
        restored = `${restored.slice(0, range.start)}${range.token.original}${restored.slice(
            range.end,
        )}`;
    });

    return restored;
};

export const formulaHasHighlight = (
    formulaText = '',
    resolvedValue = '',
    highlightTokens = [],
    {minLength = MIN_HIGHLIGHT_LENGTH} = {},
) => {
    const normalizedFormula = normalizeHighlightValue(formulaText).toLowerCase();
    const normalizedResolved = normalizeHighlightValue(resolvedValue).toLowerCase();

    return normalizeHighlightTokens(highlightTokens, {minLength}).some((token) => {
        const normalizedOriginal = normalizeHighlightValue(token.original).toLowerCase();
        const tokenResolved = normalizeHighlightValue(token.resolved).toLowerCase();

        return (
            (normalizedFormula && normalizedOriginal === normalizedFormula) ||
            (normalizedResolved && tokenResolved === normalizedResolved)
        );
    });
};
